import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Modal,
    Select,
    MenuItem,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";

const ChatContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    width: "350px",
    height: "90vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.primary[100],
    boxShadow: "-2px 0 5px rgba(0,0,0,0.1)",
    zIndex: 1000,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "8px",
}));

const InstructionsContainer = styled(Box)(({ theme }) => ({
    padding: "16px",
    backgroundColor: theme.palette.primary[200],
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    textAlign: "center",
}));

const ChatMessages = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    color: theme.palette.text.primary,
}));

const ChatInput = styled(Box)(({ theme }) => ({
    padding: "16px",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.primary[200],
}));

// All available languages and dialects
const languages = {
    English: ["English (US)", "English (UK)", "English (Canada)"],
    Español: ["Español (México)", "Español (España)", "Español (Colombia)"],
    Français: ["Français (France)", "Français (Canada)", "Français (Haiti)", "Français (Belgique)"],
    Русский: ["Русский (Россия)", "Русский (Беларусь)", "Русский (Казахстан)"],
    العربية: [
        "العربية (الفصحى)",
        "العربية (المصرية)",
        "العربية (الخليجية)",
        "العربية (الشامية)",
    ],
    中文: ["普通话 (Mandarin)", "廣東話 (Cantonese)", "吴语 (Wu)"],
    हिन्दी: ["हिन्दी (भारत)", "हिन्दी (फिजी)", "हिन्दी (मॉरिशस)"],
    Português: ["Português (Brasil)", "Português (Portugal)", "Português (Angola)"],
    日本語: ["日本語 (標準語)", "日本語 (関西弁)", "日本語 (九州弁)"],
    Caribbean: [
        "Kreyòl Ayisyen (Haitian Creole)",
        "Papiamento (Aruba, Curaçao, Bonaire)",
        "Jamaican Patois (Jamaica)",
        "Bajan Creole (Barbados)",
        "Lesser Antillean Creole French (Saint Lucia, Dominica)",
    ],
};

const USDAExpertSidebar = () => {
    const theme = useTheme();
    const [message, setMessage] = useState("");
    const [chatHistory, setChatHistory] = useState([]);
    const [isThinking, setIsThinking] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const [selectedDialect, setSelectedDialect] = useState(languages["English"][0]);
    const chatRef = useRef(null);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const handleSendMessage = async () => {
        if (message.trim() === "") return;

        const userMessage = { sender: "user", message };
        setChatHistory((prev) => [...prev, userMessage]);
        setIsThinking(true);

        try {
            // Construct a system message and final prompt
            const systemMessage = `
You are a K12 Nutrition and USDA Food Data Central expert, as well as a policy expert for Swan AI Labs and FIG K-12.
You provide expert advice on nutrition policies, product data, USDA compliance, and best practices for school nutrition programs.
All responses should be provided in ${selectedDialect}.
            `.trim();

            const finalPrompt = `${systemMessage}\nUser: ${message}\nAssistant:`;

            const response = await axios.post(
                "https://aiendpoint.swancreate.com/api/ai/gpt",
                { prompt: finalPrompt },
                { headers: { "Content-Type": "application/json" } }
            );
            const botResponse = response.data.response.trim();
            const botMessage = { sender: "bot", message: botResponse };

            setChatHistory((prev) => [...prev, botMessage]);
        } catch (error) {
            console.error("Error communicating with AI:", error);
            setChatHistory((prev) => [
                ...prev,
                { sender: "bot", message: "Sorry, I couldn't process your request. Please try again later." },
            ]);
        } finally {
            setMessage("");
            setIsThinking(false);
        }
    };

    return (
        <ChatContainer>
            {/* Instructions Section */}
            <InstructionsContainer>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "black" }}>
                    USDA Expert Assistant
                </Typography>
                <Typography variant="body2" sx={{ color: "black", mb: 1 }}>
                    I can assist with:
                </Typography>
                <ul style={{ textAlign: "left", paddingLeft: "16px", color: "black" }}>
                    <li>USDA Food Data Central product information</li>
                    <li>Nutrition policy and compliance</li>
                    <li>Best practices for K12 school programs</li>
                </ul>
            </InstructionsContainer>

            {/* Language Selection */}
            <Box sx={{ padding: "8px 16px", display: "flex", gap: "8px" }}>
                <Select
                    value={selectedLanguage}
                    onChange={(e) => {
                        setSelectedLanguage(e.target.value);
                        setSelectedDialect(languages[e.target.value][0]);
                    }}
                    sx={{
                        flex: 1,
                        "& .MuiSelect-select": { color: "black" },
                    }}
                >
                    {Object.keys(languages).map((language) => (
                        <MenuItem key={language} value={language}>
                            {language}
                        </MenuItem>
                    ))}
                </Select>
                <Select
                    value={selectedDialect}
                    onChange={(e) => setSelectedDialect(e.target.value)}
                    sx={{
                        flex: 1,
                        "& .MuiSelect-select": { color: "black" },
                    }}
                >
                    {languages[selectedLanguage].map((dialect) => (
                        <MenuItem key={dialect} value={dialect}>
                            {dialect}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {/* Chat Messages */}
            <Box sx={{ flex: 1, overflowY: "auto", padding: "16px" }} ref={chatRef}>
                {chatHistory.map((chat, index) => (
                    <Box
                        key={index}
                        mb={2}
                        display="flex"
                        justifyContent={chat.sender === "user" ? "flex-end" : "flex-start"}
                    >
                        <Paper
                            elevation={1}
                            sx={{
                                padding: "8px 12px",
                                backgroundColor: chat.sender === "user" ? "#4caf50" : "#e0e0e0",
                                color: chat.sender === "user" ? "#ffffff" : "#000000",
                                fontWeight: "bold",
                                maxWidth: "80%",
                            }}
                        >
                            {chat.message}
                        </Paper>
                    </Box>
                ))}
            </Box>

            {/* Input Field */}
            <ChatInput>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    minRows={2}
                    maxRows={4}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#cccccc" },
                            "& input, & textarea": { color: "black" },
                        },
                    }}
                    onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    sx={{ mt: 1, width: "100%", backgroundColor: "#007bff" }}
                >
                    Send
                </Button>
            </ChatInput>

            {/* Thinking Overlay */}
            <Modal open={isThinking}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                >
                    <CircularProgress color="primary" />
                </Box>
            </Modal>
        </ChatContainer>
    );
};

export default USDAExpertSidebar;
